<template>
  <v-dialog v-model="openDialog" width="860" persistent scrollable>
    <v-card class="pt-5 pb-3 generate-winner-dialog">
      <div class="px-5 d-flex justify-space-between align-center mb-5">
        <h1 class="mb-0">Gerar ganhador</h1>
        <mf-icon icon="close" color="cinza-d20" size="24" class="cursor-pointer" @click="openDialog = false" />
      </div>

      <v-card-text class="pb-0">
        <v-data-table :items="[data]" :headers="headers" hide-default-footer disable-pagination>
          <template v-slot:item="{ item }">
            <tr>
              <td>{{ item.title }}</td>
              <td>{{ DateTime.fromFormat(item.raffle_date, 'yyyy-mm-dd').toFormat('dd/mm/yyyy') }}</td>
              <td>{{ item.quantity }}</td>
              <td>{{ item.winners.length }}</td>
            </tr>
          </template>
        </v-data-table>

        <h2 class="mt-5">Número sorteado</h2>
        <p class="subtitle mb-2">Digite o número sorteado pela Loteria Federal ou sorteio próprio no campo abaixo:</p>
        <div class="raffle-input-container">
          <v-text-field
            v-model="generateWinner.secapNumber"
            :error-messages="secapNumberErrorMessages"
            auto-format
            outlined
            label="Número sorteado"
            :value="generateWinner.secapNumber"
            dense
            @keypress="isNumber"
          />
        </div>

        <h2>Configurações</h2>
        <p class="subtitle mb-2">Selecione a quantidade de números da sorte que serão sorteados e as aproximações.</p>
        <div class="raffle-input-container">
          <mf-number-input
            v-model="generateWinner.numberOfWinners"
            auto-format
            outlined
            label="Números sorteados"
            :value="generateWinner.numberOfWinners"
            :min="1"
            :error-messages="numberOfWinnersErrorMessages"
            dense
          />
        </div>

        <p class="approximation">
          Para realizar o sorteio é necessário escolher em qual sentido o sistema irá realizar a aproximação para caso o número sorteado não tenha um ganhador
          válido. No caso de geração de números em série, os números também seguirão esse sentido.
        </p>

        <div class="inputs-container d-flex align-center">
          <div class="raffle-input-container">
            <v-select v-model="generateWinner.numberSearchMode" hide-details label="Aproximação do número" outlined dense :items="approximationOptions" />
          </div>
          <div class="raffle-input-container ml-3">
            <v-select v-model="generateWinner.seriesSearchMode" hide-details label="Aproximação da série" outlined dense :items="approximationOptions" />
          </div>
        </div>

        <p class="subtitle mb-2 mt-4">
          Além da aproximação do número da sorte, é possível configurar também se números já vinculados a qualquer um dos prêmios deste sorteio podem ou não ser
          sorteados novamente.
        </p>
        <mf-toggle v-model="generateWinner.excludePastWinners" label="Ignorar números sorteados com prêmios" hide-details />

        <h2 class="mt-5 mb-4">Customização da tela de resultado</h2>
        <div class="d-flex custom-container">
          <div>
            <h3>Banner</h3>
            <mf-image-cropper
              :mb-size-limit="10"
              :upload-image="modifyBodyImg"
              :error-message="$v.generateWinner.image_url.$error ? 'Insira a imagem do banner' : ''"
              :url="generateWinner.image_url"
              :width="1000"
              :height="200"
              @remove-image="removeImage"
              @snackbar="showSnackbar"
            />

            <div v-if="generateWinner.image_url" class="image-container mt-2">
              <img :src="generateWinner.image_url" alt="" />
            </div>
          </div>

          <div>
            <h3>Cor de fundo</h3>
            <v-menu v-model="colorMenu" top nudge-bottom="110" nudge-left="20" :close-on-content-click="false">
              <template v-slot:activator="{ on }">
                <div class="color-picker cursor-pointer" :style="`background: ${generateWinner.color}`" v-on="on" />
              </template>
              <v-card>
                <v-card-text class="pa-0">
                  <v-color-picker v-model="generateWinner.color" flat />
                </v-card-text>
              </v-card>
            </v-menu>
          </div>
        </div>

        <mf-action-buttons
          class="pa-0"
          :primary-button="{
            text: 'Gerar ganhador',
            action: handleGenerateWinner,
            isVisible: true,
            isLoading: loading,
          }"
          :extra-button="{
            text: 'Cancelar',
            action: () => $emit('close'),
            isVisible: true,
            isLoading: loading,
          }"
        />
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import { minLength, required, maxLength } from 'vuelidate/lib/validators'
import { MUTATION_SINGLE_UPLOAD_BASE64 } from '@/graphql'
import { QUERY_DRAW_RAFFLE_WINNERS } from '../../graphql'
import { mapMutations, mapGetters } from 'vuex'
import { DateTime } from 'luxon'
export default {
  name: 'GenerateWinnerDialog',
  props: {
    data: {
      type: Object,
      default: () => {
        return { title: '', raffle_date: '', quantity: '', winners: [] }
      },
    },
    value: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    DateTime,
    generateWinner: {
      numberOfWinners: 1,
      excludePastWinners: true,
      numberSearchMode: 'ASC',
      seriesSearchMode: 'ASC',
      secapNumber: null,
      image_url: '',
      color: '#FFFFFFFF',
    },
    colorMenu: false,
    loading: false,
  }),
  computed: {
    ...mapGetters(['nameImage']),
    variables() {
      const { numberOfWinners, excludePastWinners, numberSearchMode, seriesSearchMode, secapNumber } = this.generateWinner
      return {
        raffle_id: this.$route.params.id,
        prize_id: this.data._id,
        number_of_winners: numberOfWinners,
        exclude_past_winners: excludePastWinners,
        number_search_mode: numberSearchMode,
        series_search_mode: seriesSearchMode,
        secap_number: String(secapNumber),
      }
    },
    openDialog: {
      get() {
        return this.value
      },
      set(value) {
        this.$emit('input', value)
      },
    },
    headers() {
      return [
        {
          text: 'Prêmio',
          value: 'prize',
          width: '30%',
          sortable: false,
        },
        {
          text: 'Data do sorteio',
          value: 'date',
          width: '15%',
          sortable: false,
        },
        {
          text: 'Quantidade',
          value: 'quantity',
          width: '15%',
          sortable: false,
        },
        {
          text: 'Ganhadores registrados',
          value: 'winners',
          width: '30%',
          sortable: false,
        },
      ]
    },
    approximationOptions() {
      return [
        {
          text: 'Posterior',
          value: 'ASC',
        },
        {
          text: 'Anterior',
          value: 'DESC',
        },
        {
          text: 'Mais próximo posterior primeiro',
          value: 'CLOSEST_ASC_FIRST',
        },
        {
          text: 'Mais próximo anterior primeiro',
          value: 'CLOSEST_DESC_FIRST',
        },
      ]
    },
    secapNumberErrorMessages() {
      if (this.$v?.generateWinner?.secapNumber.$error) {
        if (!this.$v.generateWinner.secapNumber.minLength) {
          return 'Deve conter no mínimo 7 caracteres'
        }
        if (!this.$v.generateWinner.secapNumber.maxLength) {
          return 'Deve conter no máximo 9 caracteres'
        }
        return 'Insira o número sorteado'
      }
      return ''
    },
    numberOfWinnersErrorMessages() {
      if (this.$v?.generateWinner?.numberOfWinners.$error) {
        return 'A quantidade de prêmios é inferior ao número inserido'
      }
      return ''
    },
  },
  methods: {
    ...mapMutations(['SET_GENERATE_WINNER']),
    showSnackbar(snackbar) {
      this.$snackbar(snackbar)
    },
    async modifyBodyImg({ base64, filename }) {
      await this.$apollo
        .mutate({
          mutation: MUTATION_SINGLE_UPLOAD_BASE64,
          variables: {
            filename,
            base64,
          },
          context: {
            uri: this.$microservicesUrls['upload'],
          },
        })
        .then(({ data: { singleUploadBase64 } }) => {
          const { url } = singleUploadBase64
          this.generateWinner.image_url = url
        })
    },
    removeImage() {
      this.generateWinner.image_url = ''
    },
    async handleGenerateWinner() {
      this.$v.$reset()
      this.$v.$touch()

      if (this.$v.$error) {
        this.$snackbar({ message: 'Preencha todos os campos corretamente', snackbarColor: 'error' })
        return
      }
      try {
        this.loading = true
        const {
          data: { drawRaffleWinners },
        } = await this.$apollo.query({
          query: QUERY_DRAW_RAFFLE_WINNERS,
          variables: this.variables,
          fetchPolicy: 'no-cache',
        })
        const { draw_winners, raffle_id, prize_id } = drawRaffleWinners
        this.SET_GENERATE_WINNER({
          ...this.generateWinner,
          draw_winners,
          prize_id,
          raffle_id,
          selectedPrize: this.data,
        })
        this.$router.push({ name: 'Sorteador' })
      } catch (error) {
        const errorReason = error.graphQLErrors?.[0]?.extensions?.errors?.[0]?.path
        if (errorReason === 'secap_number_not_between_raflle_series') {
          this.$snackbar({ message: `Número da sorte inserido não está entre as séries cadastradas no sorteio.`, snackbarColor: 'error' })
        } else this.$snackbar({ message: `Não foi possível gerar ganhador.`, snackbarColor: 'error' })
      } finally {
        this.loading = false
      }
    },
    isNumber(evt) {
      const charCode = evt.which ? evt.which : evt.keyCode
      if (charCode < 48 || charCode > 57) {
        evt.preventDefault()
      }
    },
  },
  validations() {
    return {
      generateWinner: {
        secapNumber: {
          required,
          minLength: minLength(7),
          maxLength: maxLength(9),
        },
        image_url: {
          required,
          minLength: minLength(2),
        },
        numberOfWinners: {
          required,
          quantityIsValid: function (value) {
            const quantity = this.data.quantity - this.data.winners.length
            return value <= quantity
          },
        },
      },
    }
  },
}
</script>

<style lang="scss" scoped>
.generate-winner-dialog {
  h1 {
    font-size: 22px;
    color: $cinza-0;
  }

  h2 {
    font-weight: 700;
    font-size: 16px;
    color: $cinza-d10;
    margin-bottom: 8px;
    line-height: 20px;
  }

  h3 {
    font-weight: 700;
    font-size: 14px;
    color: $cinza-d20;
    margin-bottom: 12px;
    line-height: 16px;
  }

  .subtitle {
    font-size: 14px;
    color: $cinza-0;
  }

  .raffle-input-container {
    width: 220px;
  }

  .approximation {
    font-size: 14px;
    color: $cinza-d10;
    line-height: 20px;
  }

  .custom-container {
    gap: 64px;

    > div {
      width: 100%;
    }
  }

  .image-container {
    width: 100%;
    height: 100%;

    img {
      display: block;
      width: 100%;
    }
  }

  .color-picker {
    height: 28px;
    width: 28px;
    border: 2px solid $marca-0;
    border-radius: 6px;
  }

  .error-message {
    color: $vermelho-0;
  }
}

::v-deep th {
  color: $cinza-d20 !important;
  font-size: 14px;
  font-weight: bold;
  background-color: $cinza-l90;
}

::v-deep tbody {
  td {
    color: $cinza-0;
    font-size: 14px;
    line-height: 1;
  }
}

::v-deep .v-input--radio-group__input {
  flex-direction: row;
  align-items: center;
  gap: 40px;
}

::v-deep .v-radio {
  margin: 0 !important;
}
</style>
